import { Grid, TextField } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useEffect, useState } from "react";
import Order from "../../actions/order";
import {
  Autocomplete,
  Box,
  CircularProgress,
  FormHelperText,
  MenuItem,
  Typography,
} from "@mui/material";
import Controls from "../controls/Controls";
import { Form, useForm } from "../customHelpers/useForm";
import produce from "immer";
import CheckIcon from "@mui/icons-material/Check";
import Customer from "../../actions/customer";

const useStyles = makeStyles(() => ({
  icon: {
    borderRadius: "50%",
    height: "40px",
    width: "40px",
  },
  autocompletePaper: {
    position: "absolute",
    zIndex: 20,
    marginTop: "2px",
    maxHeight: "10rem",
    width: "100%",
    maxWidth: "260px",
    overflow: "auto",
    borderRadius: "6px",
    backgroundColor: "#ffffff",
    padding: "2px 0",
    fontSize: "0.875rem",
    boxShadow:
      "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
    border: "1px solid #e5e7eb",
  },
  autocompleteOption: {
    cursor: "pointer",
    userSelect: "none",
    padding: "6px 8px",
    color: "#111827",
    borderBottom: "1px solid #f3f4f6",
    "&:last-child": {
      borderBottom: "none",
    },
    "&:hover": {
      backgroundColor: "#eff6ff",
      color: "#1d4ed8",
    },
  },
  fullName: {
    fontFamily: "Roboto, sans-serif",
    color: "#111827",
    fontWeight: 500,
    fontSize: "0.75rem",
  },
  phoneNo: {
    fontFamily: "Roboto, sans-serif",
    color: "#6b7280",
    fontSize: "0.75rem",
    marginLeft: "4px",
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "flex-start",
    marginTop: "12px",
    gap: "8px",
  },
  inputAndButtonContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    width: "200px",
  },
  selectedCustomerContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-start",
    marginLeft: "16px",
  },
  selectedCustomerBox: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "4px",
    whiteSpace: "nowrap",
  },
  selectedCustomerText: {
    fontSize: "0.875rem",
    display: "flex",
    alignItems: "center",
  },
}));

const ChangeCustomer = ({ NotifyMessage, setOpenPopup, id, getOrderData }) => {
  const classes = useStyles();
  const { updateOrderCustomer } = Order();
  const { viewAllCustomers } = Customer();
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [values, setValues] = useState({
    loading: false,
    reload: false,
  });

  useEffect(() => {
    getAllCustomers();
  }, []);

  const getAllCustomers = () => {
    setValues({ ...values, loading: true });
    viewAllCustomers().then((data) => {
      if (data.err) {
        NotifyMessage({
          message: data.err,
          type: "error",
        });
      } else if (data.customers) {
        setValues({
          ...values,
          loading: false,
        });
        setCustomers(data.customers);
      }
    });
  };

  const handleSubmit = () => {
    setValues({ ...values, submitting: true });
    updateOrderCustomer({ customerId: selectedCustomer.id }, id).then(
      (data) => {
        if (data.err) {
          setValues({ ...values, submitting: false });
          NotifyMessage({
            message: data.err,
            type: "error",
          });
        } else {
          NotifyMessage({
            message: "Customer successfully changed",
            type: "success",
          });
          setOpenPopup(false);
          getOrderData();
        }
      }
    );
  };

  const handleAddClick = (e) => {
    e.preventDefault();
    if (!selectedCustomer) {
      setCustomerError(true);
    } else {
      setCustomerError(false);
      handleSubmit();
    }
  };

  const handleCustomerSelect = (customer) => {
    setSelectedCustomer(customer);
  };

  const [customerError, setCustomerError] = useState(false);

  return (
    <Form onSubmit={handleAddClick}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <div style={{ display: "flex", alignItems: "flex-start" }}>
            <div className={classes.inputAndButtonContainer}>
              <Autocomplete
                sx={{ width: { xs: "100%", sm: "20%" },marginBottom: '17px' }}
                options={customers}
                disableClearable
                getOptionLabel={(option) =>
                  `${option.fullName} - ${option.phoneNo}`
                }
                renderOption={(props, option) => (
                  <li {...props} className={classes.autocompleteOption}>
                    <div className="flex items-center gap-2">
                      <div className={classes.fullName}>{option.fullName}</div>
                      <div className={classes.phoneNo}>- {option.phoneNo}</div>
                    </div>
                  </li>
                )}
                PaperProps={{
                  classes: { root: classes.autocompletePaper },
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    style={{ width: 200 }}
                    label="Select Customer"
                    variant="outlined"
                    InputProps={{
                      ...params.InputProps,
                      type: "search",
                    }}
                  />
                )}
                onChange={(event, value) => handleCustomerSelect(value)}
              />
              {customerError && (
                <FormHelperText error>Please select a customer</FormHelperText>
              )}
              <div className={classes.buttonContainer}>
                <Controls.Button
                  disabled={values.submitting ? true : false}
                  text={values.submitting ? "Changing..." : "Done"}
                  type="submit"
                />
                <Controls.Button
                  text={"Cancel"}
                  color="default"
                  onClick={() => {
                    setOpenPopup(false);
                  }}
                />
              </div>
            </div>
            {selectedCustomer && (
              <div className={classes.selectedCustomerContainer}>
                <Box className={classes.selectedCustomerBox}>
                  <Typography className={classes.selectedCustomerText}>
                    <strong>Name:</strong> {selectedCustomer.fullName}
                  </Typography>
                  <Typography className={classes.selectedCustomerText}>
                    <strong>Phone:</strong> {selectedCustomer.phoneNo}
                  </Typography>
                  <Typography className={classes.selectedCustomerText}>
                    <strong>Email:</strong> {selectedCustomer.email}
                  </Typography>
                </Box>
              </div>
            )}
          </div>
        </Grid>
      </Grid>
    </Form>
  );
};

export default ChangeCustomer;
